import {
  useInfiniteQuery,
  useQuery,
  UseQueryOptions
} from '@tanstack/react-query'

import { getAuditLogClient } from '@/api/audit-log-client'
import { getUserClient } from '@/api/user-client'
import type {
  GetAuditLogsResponse,
  GetOrgActivityHeatmapParams,
  GetOrgActivityParams,
  GetPlatformActivityParams,
  LoginAsAuditPage,
  OrgActivity,
  OrgActivityByHour,
  OrgActivityHeatmap,
  PlatformActivitySummary
} from '@/models/IActivity'

export const ACTIVITY_QUERY_IDS = {
  GET_ORG_ACTIVITY: 'GET_ORG_ACTIVITY',
  GET_ORG_ACTIVITY_HEATMAP: 'GET_ORG_ACTIVITY_HEATMAP',
  GET_ORG_ACTIVITY_BY_HOUR: 'GET_ORG_ACTIVITY_BY_HOUR',
  GET_PLATFORM_ACTIVITY: 'GET_PLATFORM_ACTIVITY',
  GET_USER_AUDIT_LOGS: 'GET_USER_AUDIT_LOGS',
  GET_ORG_LOGIN_AS_AUDIT: 'GET_ORG_LOGIN_AS_AUDIT'
}

const DEFAULT_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  retry: false,
  staleTime: 60 * 1000
} as const

/** drops undefined params so they don't end up in the query key / URL */
const compact = <T extends object>(params: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  ) as Partial<T>

/*
 * The activity operations are internal-only endpoints of user-api, called with the plain axios
 * instance of the user client.
 *
 * TODO: switch to `userClient.getOrgActivity(...)`, `getOrgActivityHeatmap`, `getOrgActivityByHour`
 * and `getPlatformActivitySummary` once `@epilot/internal-user-client` 0.7.0 is published.
 */

export const fetchOrgActivity = async (
  orgId: string,
  params: GetOrgActivityParams = {}
): Promise<OrgActivity> => {
  const res = await getUserClient().get<OrgActivity>(
    `/v2/internal/orgs/${encodeURIComponent(orgId)}/activity`,
    { params: compact(params) }
  )

  return res.data
}

export const fetchOrgActivityHeatmap = async (
  orgId: string,
  params: GetOrgActivityHeatmapParams = {}
): Promise<OrgActivityHeatmap> => {
  const res = await getUserClient().get<OrgActivityHeatmap>(
    `/v2/internal/orgs/${encodeURIComponent(orgId)}/activity/heatmap`,
    { params: compact(params) }
  )

  return res.data
}

export const fetchOrgActivityByHour = async (
  orgId: string
): Promise<OrgActivityByHour> => {
  const res = await getUserClient().get<OrgActivityByHour>(
    `/v2/internal/orgs/${encodeURIComponent(orgId)}/activity/hourly`
  )

  return res.data
}

export const fetchPlatformActivity = async (
  params: GetPlatformActivityParams = {}
): Promise<PlatformActivitySummary> => {
  const res = await getUserClient().get<PlatformActivitySummary>(
    '/v2/internal/activity/summary',
    { params: compact(params) }
  )

  return res.data
}

export const LOGIN_AS_AUDIT_PAGE_SIZE = 50

/** "login as" sessions started by epilot staff in an organization, newest first */
export const fetchOrgLoginAsAudit = async (
  orgId: string,
  cursor?: string
): Promise<LoginAsAuditPage> => {
  const res = await getUserClient().get<LoginAsAuditPage>(
    `/v2/internal/orgs/${encodeURIComponent(orgId)}/login-as-audit`,
    { params: compact({ limit: LOGIN_AS_AUDIT_PAGE_SIZE, cursor }) }
  )

  return res.data
}

export const USER_AUDIT_LOG_LIMIT = 50

/**
 * Audit log rows initiated by a user in an organization (newest first), incl. internal rows.
 */
export const fetchUserAuditLogs = async (
  orgId: string,
  userId: string
): Promise<GetAuditLogsResponse> => {
  const res = await getAuditLogClient().post<GetAuditLogsResponse>(
    '/v1/logs',
    {
      limit: USER_AUDIT_LOG_LIMIT,
      page: 1,
      visibility: 'all',
      user: { user_id: userId }
    },
    { headers: { 'x-epilot-org-id': orgId } }
  )

  return { logs: res.data?.logs ?? [], total: res.data?.total ?? 0 }
}

export const useQueryGetOrgActivity = (
  orgId: string | undefined,
  params: GetOrgActivityParams = {},
  opts?: Partial<UseQueryOptions<OrgActivity>>
) =>
  useQuery<OrgActivity>({
    queryKey: [ACTIVITY_QUERY_IDS.GET_ORG_ACTIVITY, orgId, compact(params)],
    queryFn: () => fetchOrgActivity(orgId!, params),
    enabled: !!orgId,
    ...DEFAULT_QUERY_OPTIONS,
    ...opts
  })

export const useQueryGetOrgActivityHeatmap = (
  orgId: string | undefined,
  params: GetOrgActivityHeatmapParams = {},
  opts?: Partial<UseQueryOptions<OrgActivityHeatmap>>
) =>
  useQuery<OrgActivityHeatmap>({
    queryKey: [
      ACTIVITY_QUERY_IDS.GET_ORG_ACTIVITY_HEATMAP,
      orgId,
      compact(params)
    ],
    queryFn: () => fetchOrgActivityHeatmap(orgId!, params),
    enabled: !!orgId,
    ...DEFAULT_QUERY_OPTIONS,
    ...opts
  })

export const useQueryGetOrgActivityByHour = (
  orgId: string | undefined,
  opts?: Partial<UseQueryOptions<OrgActivityByHour>>
) =>
  useQuery<OrgActivityByHour>({
    queryKey: [ACTIVITY_QUERY_IDS.GET_ORG_ACTIVITY_BY_HOUR, orgId],
    queryFn: () => fetchOrgActivityByHour(orgId!),
    enabled: !!orgId,
    ...DEFAULT_QUERY_OPTIONS,
    ...opts
  })

export const useQueryGetPlatformActivity = (
  params: GetPlatformActivityParams = {},
  opts?: Partial<UseQueryOptions<PlatformActivitySummary>>
) =>
  useQuery<PlatformActivitySummary>({
    queryKey: [ACTIVITY_QUERY_IDS.GET_PLATFORM_ACTIVITY, compact(params)],
    queryFn: () => fetchPlatformActivity(params),
    ...DEFAULT_QUERY_OPTIONS,
    ...opts
  })

export const useQueryGetUserAuditLogs = (
  orgId: string | undefined,
  userId: string | undefined,
  opts?: Partial<UseQueryOptions<GetAuditLogsResponse>>
) =>
  useQuery<GetAuditLogsResponse>({
    queryKey: [ACTIVITY_QUERY_IDS.GET_USER_AUDIT_LOGS, orgId, userId],
    queryFn: () => fetchUserAuditLogs(orgId!, userId!),
    enabled: !!orgId && !!userId,
    ...DEFAULT_QUERY_OPTIONS,
    ...opts
  })

export const useInfiniteQueryGetOrgLoginAsAudit = (orgId: string | undefined) =>
  useInfiniteQuery({
    queryKey: [ACTIVITY_QUERY_IDS.GET_ORG_LOGIN_AS_AUDIT, orgId],
    queryFn: ({ pageParam }) => fetchOrgLoginAsAudit(orgId!, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_cursor || undefined,
    enabled: !!orgId,
    ...DEFAULT_QUERY_OPTIONS
  })
